/**
 * ProfilePlanContext - Per-GMB profile plan/subscription gating
 * 
 * This context tracks the subscription status of the currently selected GMB profile
 * and provides feature gating based on the profile's active plan.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  checkSubscription,
  checkFeatureAccess,
  getPlanInfo,
  hasFeature as checkHasFeature,
  getRequiredPlan,
  PLAN_FEATURES
} from '../services/SubscriptionService';
import logger from '../utils/logger';

const ProfilePlanContext = createContext(null);

// Cache duration for subscription checks (30 seconds - short to ensure fresh data)
const CACHE_DURATION = 30 * 1000;

export const ProfilePlanProvider = ({ children }) => {
  const [currentProfile, setCurrentProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState(null);

  const cacheRef = useRef(new Map());
  const lastCheckRef = useRef(null);

  /**
   * Get cached subscription or fetch fresh
   */
  const getCachedSubscription = useCallback(async (profileKey) => {
    const cached = cacheRef.current.get(profileKey);
    const now = Date.now();

    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      return cached.data;
    }

    // Fetch fresh
    const result = await checkSubscription(profileKey);

    if (result.success) {
      cacheRef.current.set(profileKey, {
        data: result,
        timestamp: now
      });
    }

    return result;
  }, []);

  /**
   * Load subscription for a profile
   */
  const loadProfileSubscription = useCallback(async (profileKey, forceRefresh = false) => {
    if (!profileKey) {
      setSubscription(null);
      return null;
    }

    // Check cache using ref instead of state to avoid dependency issues
    const cachedResult = lastCheckRef.current;
    const now = Date.now();

    // Skip if same profile and recent check AND we have cached subscription with a plan
    // Don't cache null/failed results - always retry those
    if (!forceRefresh &&
      cachedResult?.profileKey === profileKey &&
      (now - cachedResult.timestamp) < 10000 &&
      cachedResult.subscription?.planCode) { // Check cached subscription, not state
      console.log('[ProfilePlan] ✅ Using cached subscription for', profileKey, ':', cachedResult.subscription.planCode);
      return cachedResult.subscription;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = forceRefresh
        ? await checkSubscription(profileKey)
        : await getCachedSubscription(profileKey);

      if (result.success) {
        setSubscription(result);
        // Store result in ref for caching
        lastCheckRef.current = {
          profileKey,
          timestamp: now,
          subscription: result
        };

        logger.log(`[ProfilePlan] Loaded subscription for ${profileKey}:`, {
          plan: result.planCode,
          status: result.status,
          expires: result.expiresAt
        });
      } else {
        setSubscription({
          hasSubscription: false,
          planCode: null,
          planLabel: 'No Plan',
          features: PLAN_FEATURES.basic // Show what they could get with basic
        });
        // Don't cache failed results in ref
      }

      return result;
    } catch (err) {
      logger.error('[ProfilePlan] Error loading subscription:', err);
      setError(err.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [getCachedSubscription]); // Fixed: removed subscription dependency to prevent infinite loop

  /**
   * Set the current profile and load its subscription
   */
  const selectProfile = useCallback(async (profile) => {
    if (!profile) {
      setCurrentProfile(null);
      setSubscription(null);
      return;
    }

    console.log('[ProfilePlan] 🔍 Selecting profile:', {
      name: profile.name,
      locationId: profile.locationId,
      accountId: profile.accountId,
      profileKey: profile.profileKey
    });

    // Build profile key from profile data
    const profileKey = profile.profileKey ||
      (profile.name ? profile.name : null) ||
      (profile.accountId && profile.locationId
        ? `${profile.accountId}:${profile.locationId}`
        : null);

    console.log('[ProfilePlan] 🔑 Constructed profileKey:', profileKey);

    if (!profileKey) {
      logger.warn('[ProfilePlan] Cannot select profile without key');
      return;
    }

    setCurrentProfile({
      ...profile,
      profileKey
    });

    console.log('[ProfilePlan] 📡 Loading subscription data...');
    await loadProfileSubscription(profileKey);
  }, [loadProfileSubscription]);

  /**
   * Check if current profile has access to a feature
   */
  const hasFeature = useCallback((featureName) => {
    const planCode = subscription?.planCode;
    // Default to basic plan features if no plan loaded yet
    // This allows users to navigate while plan is being fetched
    const effectivePlan = planCode || 'basic';
    return checkHasFeature(effectivePlan, featureName);
  }, [subscription]);

  /**
   * Get the current plan code
   */
  const getCurrentPlan = useCallback(() => {
    return subscription?.planCode || 'basic';
  }, [subscription]);

  /**
   * Get plan info for current subscription
   */
  const getPlanDetails = useCallback(() => {
    const planCode = subscription?.planCode;
    return planCode ? getPlanInfo(planCode) : PLAN_FEATURES.basic;
  }, [subscription]);

  /**
   * Trigger upgrade modal for a specific feature
   */
  const triggerUpgrade = useCallback((featureName) => {
    const requiredPlan = getRequiredPlan(featureName);
    setUpgradeFeature({
      name: featureName,
      requiredPlan,
      currentPlan: subscription?.planCode || null
    });
    setShowUpgradeModal(true);
  }, [subscription]);

  /**
   * Close upgrade modal
   */
  const closeUpgradeModal = useCallback(() => {
    setShowUpgradeModal(false);
    setUpgradeFeature(null);
  }, []);

  /**
   * Check feature with upgrade prompt
   * Returns true if allowed, false if blocked (and shows upgrade modal)
   */
  const checkFeatureWithUpgrade = useCallback((featureName) => {
    if (hasFeature(featureName)) {
      return true;
    }

    triggerUpgrade(featureName);
    return false;
  }, [hasFeature, triggerUpgrade]);

  /**
   * Refresh subscription data
   */
  const refreshSubscription = useCallback(async () => {
    if (currentProfile?.profileKey) {
      // Clear cache for this profile
      cacheRef.current.delete(currentProfile.profileKey);
      await loadProfileSubscription(currentProfile.profileKey, true);
    }
  }, [currentProfile, loadProfileSubscription]);

  // Auto-load subscription when selected profile changes in localStorage
  useEffect(() => {
    const loadFromStorage = () => {
      try {
        const selectedBusiness = localStorage.getItem('selectedBusiness');
        if (selectedBusiness) {
          const business = JSON.parse(selectedBusiness);
          if (business.name && business.name !== currentProfile?.profileKey) {
            selectProfile({
              profileKey: business.name,
              name: business.title || business.storefrontAddress?.addressLines?.[0],
              ...business
            });
          }
        }
      } catch (err) {
        logger.error('[ProfilePlan] Error loading from storage:', err);
      }
    };

    loadFromStorage();

    // Listen for storage changes (cross-tab)
    const handleStorage = (e) => {
      if (e.key === 'selectedBusiness') {
        loadFromStorage();
      }
    };

    // Listen for same-tab auth changes (custom event from authUtils.setSession)
    // This ensures subscription is refreshed after login
    const handleAuthChanged = () => {
      logger.log('[ProfilePlan] Auth changed event received, refreshing subscription');
      loadFromStorage();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('visibeen:auth-changed', handleAuthChanged);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('visibeen:auth-changed', handleAuthChanged);
    };
  }, [selectProfile, currentProfile?.profileKey]);

  const value = {
    // State
    currentProfile,
    subscription,
    isLoading,
    error,
    showUpgradeModal,
    upgradeFeature,

    // Actions
    selectProfile,
    loadProfileSubscription,
    refreshSubscription,

    // Feature checking
    hasFeature,
    checkFeatureWithUpgrade,
    getCurrentPlan,
    getPlanDetails,

    // Upgrade modal
    triggerUpgrade,
    closeUpgradeModal,

    // Plan info
    planCode: subscription?.planCode || null,
    planLabel: subscription?.planLabel || 'No Plan',
    hasSubscription: subscription?.hasSubscription || false,
    expiresAt: subscription?.expiresAt,
    daysRemaining: subscription?.daysRemaining,
    isExpiringSoon: subscription?.daysRemaining <= 7 && subscription?.daysRemaining > 0,
    isExpired: !subscription?.hasSubscription || subscription?.status === 'expired'
  };

  return (
    <ProfilePlanContext.Provider value={value}>
      {children}
    </ProfilePlanContext.Provider>
  );
};

/**
 * Hook to access the ProfilePlan context
 */
export const useProfilePlan = () => {
  const context = useContext(ProfilePlanContext);

  if (!context) {
    // Return default values if context not available
    return {
      currentProfile: null,
      subscription: null,
      isLoading: false,
      error: null,
      showUpgradeModal: false,
      upgradeFeature: null,
      selectProfile: async () => { },
      loadProfileSubscription: async () => { },
      refreshSubscription: async () => { },
      hasFeature: () => false,
      checkFeatureWithUpgrade: () => false,
      getCurrentPlan: () => null,
      getPlanDetails: () => PLAN_FEATURES.basic,
      triggerUpgrade: () => { },
      closeUpgradeModal: () => { },
      planCode: null,
      planLabel: 'No Plan',
      hasSubscription: false,
      expiresAt: null,
      daysRemaining: 0,
      isExpiringSoon: false,
      isExpired: true
    };
  }

  return context;
};

export default ProfilePlanContext;
